(function () {
  'use strict';

  if (window.DieuxMediaCenter) return;

  const FRAME_SELECTOR = '[data-media-center]';
  const CLONE_SELECTOR = '[data-swiper-loop-clone]';
  const PROPERTY = '--carousel-media-center';

  const frames = new Map();

  /**
   * The media box a frame centres its controls on.
   *
   * @param {Element} frame - Frame carrying `data-media-center`.
   * @returns {Element|null} First laid-out match outside a loop clone, or `null`.
   */
  function mediaBox(frame) {
    const selector = frame.getAttribute('data-media-center');
    if (!selector) return null;

    let candidates;
    try {
      candidates = frame.querySelectorAll(selector);
    } catch {
      return null;
    }

    for (const candidate of candidates) {
      if (candidate.closest(CLONE_SELECTOR)) continue;
      if (!candidate.getClientRects().length) continue;
      return candidate;
    }

    return null;
  }

  /**
   * Points a frame's record at the box it now measures.
   *
   * @param {Element} frame - Registered frame.
   * @param {Element|null} box - Box to observe, or `null` to observe none.
   * @returns {void}
   */
  function trackBox(frame, box) {
    const record = frames.get(frame);
    if (!record || record.box === box) return;
    if (record.box) record.sizes.unobserve(record.box);
    if (box) record.sizes.observe(box);
    record.box = box;
  }

  /**
   * Writes a frame's measured centre, or clears it so the control falls back to the slide midpoint.
   *
   * @param {Element} frame - Registered frame.
   * @param {number|null} centre - Offset in pixels from the frame's block start, or `null`.
   * @returns {void}
   */
  function apply(frame, centre) {
    window.DieuxFrame.write(() => {
      if (!frame.isConnected) return;
      if (centre === null) {
        frame.style.removeProperty(PROPERTY);
        delete frame.dataset.mediaCenterState;
        return;
      }
      frame.style.setProperty(PROPERTY, `${centre}px`);
      frame.dataset.mediaCenterState = 'media';
    });
  }

  /**
   * Measures one frame's media midpoint.
   *
   * @param {Element} frame - Registered frame.
   * @returns {void}
   */
  function measureFrame(frame) {
    if (!frame.isConnected) {
      release(frame);
      return;
    }

    const box = mediaBox(frame);
    trackBox(frame, box);

    if (!box) {
      apply(frame, null);
      return;
    }

    const frameRect = frame.getBoundingClientRect();
    const boxRect = box.getBoundingClientRect();
    if (!frameRect.height || !boxRect.height) {
      apply(frame, null);
      return;
    }

    const centre = boxRect.top - frameRect.top + boxRect.height / 2;
    apply(frame, Math.round(centre * 100) / 100);
  }

  /**
   * Queues a frame's measurement into the shared read phase.
   *
   * @param {Element} frame - Registered frame.
   * @returns {void}
   */
  function schedule(frame) {
    const record = frames.get(frame);
    if (!record) return;
    window.DieuxFrame.measure(record.key, () => measureFrame(frame));
  }

  /**
   * Starts tracking a frame.
   *
   * @param {Element} frame - Frame carrying `data-media-center`.
   * @returns {void}
   */
  function register(frame) {
    if (frames.has(frame)) return;

    const requeue = () => schedule(frame);
    const record = {
      key: Symbol('carousel-media-center'),
      box: null,
      sizes: new ResizeObserver(requeue),
      children: new MutationObserver(requeue),
      requeue,
    };

    frames.set(frame, record);
    record.sizes.observe(frame);
    record.children.observe(frame, { childList: true, subtree: true });
    // `load` does not bubble, so the frame listens for it in the capture phase.
    frame.addEventListener('load', requeue, true);
    schedule(frame);
  }

  /**
   * Stops tracking a frame.
   *
   * @param {Element} frame - Previously registered frame.
   * @returns {void}
   */
  function release(frame) {
    const record = frames.get(frame);
    if (!record) return;
    record.sizes.disconnect();
    record.children.disconnect();
    frame.removeEventListener('load', record.requeue, true);
    window.DieuxFrame.cancelMeasure(record.key);
    frames.delete(frame);
  }

  /**
   * Registers every frame inside a root, and drops any that have left the document.
   *
   * @param {Element|Document} [root] - Subtree to scan. Defaults to the document.
   * @returns {void}
   */
  function scan(root) {
    const scope = root instanceof Element || root instanceof Document ? root : document;

    frames.forEach((_, frame) => {
      if (!frame.isConnected) release(frame);
    });

    if (scope instanceof Element && scope.matches(FRAME_SELECTOR)) register(scope);
    scope.querySelectorAll(FRAME_SELECTOR).forEach(register);
  }

  /**
   * Re-measures every tracked frame.
   *
   * @returns {void}
   */
  function refresh() {
    frames.forEach((_, frame) => schedule(frame));
  }

  /**
   * Registers frames that arrive after the first scan.
   *
   * Deferred sections, Section Rendering swaps, and editor patches all insert a frame with no
   * event this module could otherwise hear, so added subtrees are inspected directly.
   *
   * @param {MutationRecord[]} records - Batch from the document observer.
   * @returns {void}
   */
  function adopt(records) {
    let swept = false;

    records.forEach((record) => {
      record.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;
        if (node.matches(FRAME_SELECTOR)) register(node);
        node.querySelectorAll(FRAME_SELECTOR).forEach(register);
      });

      if (!swept && record.removedNodes.length) {
        swept = true;
        frames.forEach((_, frame) => {
          if (!frame.isConnected) release(frame);
        });
      }
    });
  }

  function start() {
    scan(document);

    new MutationObserver(adopt).observe(document.documentElement, {
      childList: true,
      subtree: true,
    });

    // Loop clones and slide widths only exist once the shared controller has mounted.
    window.addEventListener('dieux:swiper:ready', () => {
      scan(document);
      refresh();
    });

    document.addEventListener('shopify:section:load', (event) => {
      scan(event.target instanceof Element ? event.target : document);
    });
    document.addEventListener('shopify:block:select', refresh);

    document.fonts?.ready.then(refresh).catch(() => {});
  }

  window.DieuxMediaCenter = Object.freeze({ scan, refresh });

  if (window.DieuxFrame) {
    start();
  } else {
    window.addEventListener('dieux:frame:ready', start, { once: true });
  }
})();
